import { useGetMySubscriptionsQuery } from 'api/subscriptionPlanEndpoints';
import { UserRole } from 'api/dtos/user';
import { ActiveSubscription } from 'Components/Common/models/ActiveSubscription';
import useGetUser from 'hooks/useGetUser';
import { useMemo } from 'react';

const requiresPaymentAction = (status?: string, paymentStatus?: string, requiresAction?: boolean) => (
    Boolean(requiresAction) ||
    status?.toLowerCase() === 'past_due' ||
    status?.toLowerCase() === 'unpaid' ||
    status?.toLowerCase() === 'incomplete' ||
    paymentStatus?.toLowerCase() === 'payment_required'
);

const useGetCurrentSubscription = () => {
    const {
        data: subscriptions,
        isFetching,
        isError,
        refetch,
    } = useGetMySubscriptionsQuery();
    const user = useGetUser();

    const activePaid = useMemo(() => subscriptions?.find(
        s =>
            typeof s.amount === 'number' &&
        s.amount > 0 &&
        s.currentPeriodEnd &&
        (new Date(s.currentPeriodEnd) > new Date()),
    ), [subscriptions]);
    const paymentRequiredSubscription = useMemo(() => subscriptions?.find(
        s => requiresPaymentAction(
            s.status,
            typeof s.paymentStatus === 'string' ? s.paymentStatus : undefined,
            s.requiresPaymentAction === true,
        ),
    ), [subscriptions]);

    const activeSubscription: ActiveSubscription | null = useMemo(() => {
        const active =
            paymentRequiredSubscription ??
            activePaid ??
            subscriptions?.find(
                s =>
                    s.currentPeriodStart &&
                    new Date(s.currentPeriodStart) <= new Date() &&
                (!s.currentPeriodEnd ||
                new Date(s.currentPeriodEnd) > new Date()),
            ) ??
            null;

        const nextUserSubscription =
            active
                ? subscriptions?.find(
                    s =>
                        s.currentPeriodStart &&
                        active.currentPeriodStart &&
                        new Date(s.currentPeriodStart) >
                    new Date(active.currentPeriodStart),
                ) ?? null
                : null;

        return active
            ? {
                ...active,
                nextUserSubscription,
            }
            : null;
    }, [activePaid, paymentRequiredSubscription, subscriptions]);

    const ownedEntitiesCount = user?.entities.filter((entity) => entity.role === UserRole.Owner).length ?? 0;
    const isReachedLimit = ownedEntitiesCount >= (activeSubscription?.maxEntities ?? 0);

    const isPaymentRequired = Boolean(paymentRequiredSubscription);
    const isPaidPlan = Boolean(activePaid || paymentRequiredSubscription);

    return {
        activeSubscription,
        isPaidPlan,
        isPaymentRequired,
        isFetching,
        isError,
        refetch,
        isReachedLimit,
    };
};

export default useGetCurrentSubscription;
